import React from 'react';
import {
	Card,
	Col,
	Row,
} from 'antd';
import { Moment } from 'moment';
import {
	CapexOpexInput,
	CurrencySymbols,
} from '@shared/utils/constants';
import {
	nowMoment,
	toMoment,
} from '@shared/utils/date';
import { formatCurrency } from '@shared/utils/currency';
import { standardSort } from '@shared/utils/standardSort';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { VesselFinanceEntryProps } from '@api/models/vessel-finance-entry';
import { formatDate } from '@client/utils/formatDate';
import {
	deleteVesselFinancialData,
	createVesselFinancialData,
	updateVesselFinancialData,
} from '@client/lib/api';
import EditableTable from '@client/components/EditableTable';
import { useAuth } from '@client/lib/auth';

type VesselFinanceRow = VesselFinanceEntryProps & {
	momentYear: Moment;
	momentMonth: Date | Moment | null;
};

type VesselFinanceTabProps = {
	vessel: GetVesselDetailsResponse | undefined;
	vesselId: number;
	refreshVessel: () => void;
};

export const VesselFinanceTab = ({
	vessel,
	vesselId,
	refreshVessel,
}: VesselFinanceTabProps) => {
	const { userInfo } = useAuth();

	const saveFinancialDetails = async ({ capex, opex, momentYear, momentMonth }: VesselFinanceRow) => {
		await createVesselFinancialData(
			vesselId,
			momentYear ? toMoment(momentYear).year() : null,
			momentMonth ? toMoment(momentMonth).format('YYYY-MM-01') : null,
			capex,
			opex,
		);
		await refreshVessel();
	};

	const deleteFinancialDetailsEntry = async (id: number) => {
		await deleteVesselFinancialData(vesselId, id);
		await refreshVessel();
	};

	const updateFinancialDetailsEntry = async (entryId: number, values: VesselFinanceRow) => {
		await updateVesselFinancialData(vesselId, entryId, {
			...values,
			year: values.momentYear ? toMoment(values.momentYear).year() : null,
		});
		await refreshVessel();
	};

	const vesselFinanceEntries: VesselFinanceRow[] = (vessel?.vesselFinanceEntries ?? []).map((entry) => {
		const momentYear = nowMoment().set('year', entry.year ?? 0);
		const momentMonth = entry.month;

		return {
			...entry,
			momentYear,
			momentMonth,
		};
	});

	return (
		<Row gutter={[16, 16]}>
			{vessel?.ownershipType !== 'tcIn' && (
				<Col span={12}>
					<Card title="Vessel Finance">
						<EditableTable<VesselFinanceRow, 'id'>
							pagination={false}
							emptyText="Add Opex & Capex"
							keyDataIndex="id"
							key={vesselFinanceEntries.length}
							iconButtons
							size="small"
							dataSource={vesselFinanceEntries}
							onSave={updateFinancialDetailsEntry}
							enableDelete
							allowAddNew
							onAddNew={saveFinancialDetails}
							addNewText="Vessel Opex & Capex"
							onDelete={deleteFinancialDetailsEntry}
							columns={[
								...(('capexOpexInput' in userInfo && userInfo.capexOpexInput === CapexOpexInput.Yearly) ?
									[
										{
											dataIndex: 'momentYear',
											title: 'Year',
											sorter: standardSort('year'),
											sortOrder: 'ascend' as const,
											render: (c: Moment | null) => (c == null ? (<em>Empty</em>) : formatDate(c, 'YYYY')),
											editingProps: {
												type: 'date' as const,
												required: true,
												inputProps: {
													allowClear: false,
													picker: 'year' as const,
													format: 'YYYY',
												},
											},
											editable: true,
										},
									] :
									[
										{
											dataIndex: 'momentMonth',
											title: 'Month',
											sorter: standardSort('month'),
											sortOrder: 'ascend' as const,
											width: 170,
											render: (c: Moment | null) => (c == null ? (<em>Empty</em>) : formatDate(c, 'MMMM YYYY')),
											editingProps: {
												type: 'date' as const,
												required: true,
												inputProps: {
													allowClear: false,
													picker: 'month' as const,
													format: 'MMMM YYYY',
												},
											},
											editable: true,
										},
									]
								),
								{
									dataIndex: 'capex',
									title: `CAPEX ${CurrencySymbols[userInfo.baseCurrency as keyof typeof CurrencySymbols]}/day`,
									render: (c: number | null) => (
										c == null ? (
											<em>Empty</em>
										) : formatCurrency(c, userInfo.baseCurrency)
									),
									editingProps: {
										type: 'currency' as const,
										required: true,
										inputProps: {
											placeholder: '',
										},
									},
									editable: true,
								},
								{
									dataIndex: 'opex',
									title: `OPEX ${CurrencySymbols[userInfo.baseCurrency as keyof typeof CurrencySymbols]}/day`,
									render: (c: number | null) => (
										c == null ? (
											<em>Empty</em>
										) : formatCurrency(c, userInfo.baseCurrency)
									),
									editingProps: {
										type: 'currency' as const,
										required: true,
										inputProps: {
											placeholder: '',
										},
									},
									editable: true,
								},
							]}
						/>
					</Card>
				</Col>
			)}
		</Row>
	);
};
